import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { DISTRICTS, LOCAL_BODY_TYPES } from "@/data/static";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Search } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { isAdminRole } from "@/lib/roles";
import { toast } from "sonner";

export default function Directory() {
  const { user, ready } = useAuth();
  const [filters, setFilters] = useState({ q: "", district: "all", local_body: "all" });
  const [members, setMembers] = useState([]);

  useEffect(() => {
    if (!user || !isAdminRole(user.role)) return;
    const params = {};
    if (filters.q) params.q = filters.q;
    if (filters.district !== "all") params.district = filters.district;
    if (filters.local_body !== "all") params.local_body = filters.local_body;
    api.get("/directory", { params })
      .then(r => setMembers(r.data))
      .catch(() => setMembers([]));
  }, [filters, user]);

  if (!ready) return null;
  if (!user) {
    toast.error("Please sign in to view the Member Directory");
    return <Navigate to="/login" replace />;
  }
  if (!isAdminRole(user.role)) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center px-4">
          <Card className="p-8 max-w-md text-center" data-testid="directory-forbidden">
            <div className="text-xs uppercase tracking-widest text-brand-blue font-semibold">Restricted Area</div>
            <h1 className="text-2xl font-black tracking-tight mt-2">Member Directory is admin-only</h1>
            <p className="text-sm text-muted-foreground mt-3">
              To protect member privacy, the KELTA Member Directory is visible only to Super Admin,
              State-level and District-level office bearers.
            </p>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 py-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="mb-8">
            <div className="text-xs uppercase tracking-widest text-brand-blue font-semibold">Member Directory</div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Find a KELTA member</h1>
          </div>

          <Card className="p-4 mb-6">
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="relative">
                <Search className="absolute w-4 h-4 left-3 top-3 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Search by name / ID / local body"
                  value={filters.q}
                  onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
                  data-testid="dir-search"
                />
              </div>
              <Select value={filters.district} onValueChange={v => setFilters(f => ({ ...f, district: v }))}>
                <SelectTrigger data-testid="dir-district"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All districts</SelectItem>
                  {DISTRICTS.map(d => <SelectItem key={d.slug} value={d.name}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.local_body} onValueChange={v => setFilters(f => ({ ...f, local_body: v }))}>
                <SelectTrigger data-testid="dir-lb"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All local body types</SelectItem>
                  {LOCAL_BODY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </Card>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {members.map(m => (
              <Card key={m.id || m.membership_id} className="p-4 hover-lift flex items-center gap-3">
                <Avatar className="w-12 h-12">
                  <AvatarImage src={m.photo_url} />
                  <AvatarFallback>{(m.full_name || "?")[0]}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{m.full_name}</div>
                  {m.role && m.role !== "member" && (
                    <Badge variant="outline" className="text-[9px] mt-0.5 border-brand-blue/30 text-brand-blue" data-testid={`dir-role-${m.id}`}>
                      {m.role.replace(/_/g, " ")}
                    </Badge>
                  )}
                  <div className="text-xs text-muted-foreground truncate">{m.designation}</div>
                  <div className="text-xs text-muted-foreground truncate">{m.local_body_name} • {m.district}</div>
                  <div className="text-[10px] font-mono text-brand-blue mt-0.5">{m.membership_id}</div>
                </div>
              </Card>
            ))}
            {members.length === 0 && (
              <div className="col-span-full text-center text-muted-foreground py-16 text-sm">
                No active members match this filter yet.
              </div>
            )}
          </div>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}
